import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Button, Center, Dialog, Separator, Heading, HStack, Input, Link, Slider, Spinner, Text, VStack, Field } from '@chakra-ui/react';
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea';
import { SelectableButtons, SelectableButtonsOption } from '@src/components/sco/SelectableButtons';
import { SkillsComboboxInput } from '@src/components/sco/SkillsComboboxInput';
import { DeleteIcon } from '@src/components/sco/icons';
import { userRoles } from '@src/constants/user-roles.ts';
import { ScoUser, ScoUserSkill, UpsertUserSkillInput } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LuCheck, LuPlus, LuWandSparkles } from 'react-icons/lu';
export const UserSkillsEditor = props => {
  const $ = _c(20);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [userSkills, setUserSkills] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = gql`
    query {
      viewer {
        currentUser {
          id
          bio
          linkedin
          userSkills(order: [["experienceStrength", "desc"]]) {
            id
            userSkillId
            skill {
              id
              skillId
              title
              description
            }
            compensationTypeKeys
            timeCommitmentTypeKeys
            userRoleKeys
            skillId
            title
            description
            experienceStrength
            experienceNotes
          }
        }
      }
    }
  `;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const query = useQuery(t1);
  const user = query.data?.viewer?.currentUser || null;
  const queryUserSkills = query.data?.viewer?.currentUser?.userSkills || null;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setUserSkills(_temp);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const addSkill = t2;
  let t3;
  let t4;
  if ($[3] !== queryUserSkills) {
    t3 = () => {
      if (queryUserSkills) {
        setUserSkills(queryUserSkills);
      }
    };
    t4 = [queryUserSkills];
    $[3] = queryUserSkills;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== props.refetchTrigger || $[7] !== query) {
    t5 = () => {
      if (props.refetchTrigger) {
        query.refetch();
      }
    };
    $[6] = props.refetchTrigger;
    $[7] = query;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== props.refetchTrigger) {
    t6 = [props.refetchTrigger];
    $[9] = props.refetchTrigger;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useEffect(t5, t6);
  if (!queryUserSkills) {
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Center padding="1em"><Spinner /></Center>;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [4, 4, 8];
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Box marginBottom={4}><Heading size="lg">Your Skills</Heading><Text>Tell us about what you're good at and we'll connect you with other great members.</Text></Box>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== props || $[15] !== query || $[16] !== user?.bio || $[17] !== user?.linkedin || $[18] !== userSkills) {
    t9 = <Box bgColor="colorPalette.container" borderRadius="lg" boxShadow="md" padding={t7} width="full">{t8}{userSkills.length === 0 ? <VStack bgColor="containerSecondary" borderRadius="md" gap={4} justify="center" padding={6}><Text>You haven't added any skills yet.</Text><HStack><ExtractSkillsAndExperiencesFromBioModal bio={user?.bio} linkedinUrl={user?.linkedin} onComplete={() => {
            query.refetch();
            if (props.onDetectionCompleted) {
              props.onDetectionCompleted();
            }
          }} /></HStack><Link color="colorPalette.fg/50" fontSize="sm" variant="underline" onClick={addSkill}>Manually Add Your First Skill</Link></VStack> : <VStack align="stretch" gap={4}>{userSkills.map(_temp3)}<Button alignSelf="center" onClick={addSkill}><LuPlus />Add Another Skill</Button></VStack>}</Box>;
    $[14] = props;
    $[15] = query;
    $[16] = user?.bio;
    $[17] = user?.linkedin;
    $[18] = userSkills;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
};
const sliderMarks = [[10, 'Novice'], [30, 'Learning'], [50, 'Familiar'], [70, 'Proficient'], [90, 'Expert']];
const timeCommitmentOptions = [{
  title: 'Full-Time',
  key: 'full-time'
}, {
  title: 'Part-time',
  key: 'part-time'
}, {
  title: 'One-Time',
  key: 'one-time'
}];
const compensationOptions = [{
  title: 'Free',
  key: 'free'
}, {
  title: 'Cash',
  key: 'cash'
}, {
  title: 'Equity',
  key: 'equity'
}, {
  title: 'Revenue Share',
  key: 'revenue-share'
}];
export const UserSkillEditorItem = (props: {
  index?: number;
  onChange: (userSkill: Partial<ScoUserSkill>) => any;
  userSkill: ScoUserSkill;
}) => {
  const sliderTrackRef = useRef<HTMLDivElement>(null);
  const [userSkill, setUserSkill] = useState<Partial<ScoUserSkill>>(props.userSkill);
  const [upsertUserSkill, upsertUserSkillMutation] = useMutation(gql`
    mutation ($input: UpsertUserSkillInput!) {
      upsertUserSkill(input: $input) {
        userSkill {
          id
          skillId
          userSkillId
          skill {
            id
            title
          }
          compensationTypeKeys
          timeCommitmentTypeKeys
          userRoleKeys
          experienceNotes
          experienceStrength
          deletedAt
        }
      }
    }
  `);
  const updateUserSkill = useCallback((values?: Partial<ScoUserSkill>) => {
    setUserSkill(prev => ({
      ...prev,
      ...values
    }));
  }, [setUserSkill]);
  const onSubmit = useCallback(async (userSkill_0: Partial<ScoUserSkill>) => {
    const input: UpsertUserSkillInput = {
      userSkillId: userSkill_0.userSkillId,
      skillId: userSkill_0.skillId,
      title: userSkill_0.title,
      experienceStrength: userSkill_0.experienceStrength,
      experienceNotes: userSkill_0.experienceNotes,
      userRoleKeys: userSkill_0.userRoleKeys,
      compensationTypeKeys: userSkill_0.compensationTypeKeys,
      timeCommitmentTypeKeys: userSkill_0.timeCommitmentTypeKeys,
      deletedAt: userSkill_0.deletedAt
    };
    await upsertUserSkill({
      variables: {
        input
      }
    });
  }, []);
  const restoreUserSkill = useCallback(async (userSkill_1: Partial<ScoUserSkill>) => {
    if (userSkill_1.userSkillId) {
      await upsertUserSkill({
        variables: {
          input: {
            userSkillId: userSkill_1.userSkillId,
            deletedAt: null
          }
        }
      });
    }
    updateUserSkill({
      deletedAt: null
    });
  }, []);
  const deleteUserSkill = useCallback(async (userSkill_2: Partial<ScoUserSkill>) => {
    if (userSkill_2.userSkillId) {
      await upsertUserSkill({
        variables: {
          input: {
            userSkillId: userSkill_2.userSkillId,
            deletedAt: new Date()
          }
        }
      });
    }
    updateUserSkill({
      deletedAt: new Date()
    });
  }, []);
  const handleUserRoleChange = useCallback((selections: SelectableButtonsOption[]) => {
    setUserSkill(prev_0 => ({
      ...prev_0,
      userRoleKeys: selections.map(s => s.key)
    }));
  }, []);
  const handleTimeCommitmentChange = useCallback((selections_0: SelectableButtonsOption[]) => {
    setUserSkill(prev_1 => ({
      ...prev_1,
      timeCommitmentTypeKeys: selections_0.map(s_0 => s_0.key)
    }));
  }, []);
  const handleCompensationChange = useCallback((selections_1: SelectableButtonsOption[]) => {
    setUserSkill(prev_2 => ({
      ...prev_2,
      compensationTypeKeys: selections_1.map(s_1 => s_1.key)
    }));
  }, []);
  useEffect(() => {
    props.onChange(userSkill);
  }, [userSkill]);
  if (userSkill.deletedAt) {
    return <Center key={props.index} bgColor={'containerSecondary'} borderRadius={'md'} padding={[4, 4, 6]}>
        <Text>
          Deleted "{userSkill.title}
          {'" •'}{' '}
          <Link variant={'underline'} onClick={() => restoreUserSkill(userSkill)}>
            Restore
          </Link>
        </Text>
      </Center>;
  }
  return <Box key={props.index} bgColor={'colorPalette.muted/50'} border={'1px solid'} borderColor={'colorPalette.border'} borderRadius={'md'} boxShadow={'sm'} id={`skill-editor-${props.index}`} padding={[4, 4, 6]}>
      <VStack align={'stretch'} gap={4}>
        <Field.Root required flexGrow={1}>
          <Field.Label htmlFor={`skill-name-${props.index}`}>Skill</Field.Label>
          <SkillsComboboxInput defaultValue={userSkill.skill ? [{
          key: userSkill.skillId,
          title: userSkill.skill.title
        }] : []} comboboxTagInputProps={{
          allowAddingItems: true,
          allowMultipleSelections: false,
          renderSelectedItems: false,
          placeholder: 'Search skills...'
        }} onChange={skillItems => {
          if (skillItems[0]) {
            updateUserSkill({
              title: skillItems[0].title,
              skillId: skillItems[0].key
            });
          } else {
            updateUserSkill({
              skillId: null
            });
          }
        }} />
        </Field.Root>

        <Field.Root>
          <Field.Label htmlFor={`skill-notes-${props.index}`}>Notes</Field.Label>
          <AutoResizeTextarea id={`skill-notes-${props.index}`} minHeight={'3em'} placeholder={'Add any relevant notes, like projects where you used this skill...'} size={'sm'} value={userSkill.experienceNotes || ''} onChange={e => updateUserSkill({
          experienceNotes: e.target.value
        })} />
        </Field.Root>

        <Field.Root>
          <Field.Label htmlFor={`skill-strength-${props.index}`}>
            Experience Rating {userSkill.experienceStrength ?? 50}%
          </Field.Label>
          <Box colorPalette={'orange'} width={'full'}>
            <Slider.Root defaultValue={[userSkill.experienceStrength ?? 50]} id={`skill-strength-${props.index}`} max={100} min={0} minHeight={'4em'} size={'lg'} step={10} value={[userSkill.experienceStrength ?? 50]} onValueChange={details => {
            updateUserSkill({
              experienceStrength: details.value[0] ?? 50
            });
          }}>
              <Slider.Control>
                <Slider.Track ref={sliderTrackRef}>
                  <Slider.Range />
                </Slider.Track>
                <Slider.Thumb color={'black'} fontWeight={'bold'} index={0}>
                  <Slider.HiddenInput />
                </Slider.Thumb>
                <Slider.MarkerGroup>
                  {sliderMarks.map(([value, label]: [number, string], idx) => <Slider.Marker key={idx} value={value}>
                      <Slider.MarkerLabel marginTop={8}>{label}</Slider.MarkerLabel>
                    </Slider.Marker>)}
                </Slider.MarkerGroup>
              </Slider.Control>
            </Slider.Root>
          </Box>
        </Field.Root>
        <Separator />
        <Field.Root>
          <HStack align={'center'}>
            <Field.Label htmlFor={`skill-user-role-${props.index}`}>
              I will offer this skill as a:
            </Field.Label>
            <HStack>
              <SelectableButtons defaultValue={userSkill.userRoleKeys || []} options={userRoles} selectableButtonProps={{
              buttonProps: {
                paddingY: 2,
                paddingX: 4,
                size: 'xs',
                flexGrow: 1
              },
              icons: {
                default: LuPlus,
                selected: LuCheck
              },
              iconPlacement: 'left'
            }} onChange={handleUserRoleChange} />
            </HStack>
          </HStack>
        </Field.Root>
        <Separator />
        <Field.Root>
          <HStack align={'center'}>
            <Field.Label htmlFor={`skill-user-role-${props.index}`}>I can commit to:</Field.Label>
            <SelectableButtons defaultValue={userSkill.timeCommitmentTypeKeys || []} options={timeCommitmentOptions} selectableButtonProps={{
            buttonProps: {
              paddingY: 2,
              paddingX: 4,
              size: 'xs',
              flexGrow: 1
            },
            icons: {
              default: LuPlus,
              selected: LuCheck
            },
            iconPlacement: 'left'
          }} onChange={handleTimeCommitmentChange} />
          </HStack>
        </Field.Root>
        <Separator />
        <Field.Root>
          <HStack align={'center'}>
            <Field.Label htmlFor={`skill-user-role-${props.index}`}>
              I want to be compensated with:
            </Field.Label>
            <SelectableButtons defaultValue={userSkill.compensationTypeKeys || []} options={compensationOptions} selectableButtonProps={{
            buttonProps: {
              paddingY: 2,
              paddingX: 4,
              size: 'xs',
              flexGrow: 1
            },
            icons: {
              default: LuPlus,
              selected: LuCheck
            },
            iconPlacement: 'left'
          }} onChange={handleCompensationChange} />
          </HStack>
        </Field.Root>

        <Separator />
        <HStack justify={'flex-end'}>
          <Button loading={upsertUserSkillMutation.loading} variant={'cta'} onClick={() => onSubmit(userSkill)}>
            Save Skill
          </Button>
          <Button aria-label={'Delete Skill'} variant={'outline'} onClick={() => deleteUserSkill(userSkill)}>
            <DeleteIcon />
            Delete
          </Button>
        </HStack>
      </VStack>
    </Box>;
};
export const ExtractSkillsAndExperiencesFromBioModal = (props: {
  bio: string;
  ctaLabel?: string;
  linkedinUrl: string;
  onComplete?: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);
  const [bio, setBio] = useState<string>(props.bio);
  const [linkedinUrl, setLinkedinUrl] = useState<string>(props.linkedinUrl);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [extractSkillsForUser, extractSkillsForUserMutation] = useMutation(gql`
    mutation ($input: ExtractSkillsForUserInput!) {
      extractSkillsForUser(input: $input) {
        user {
          id
        }
      }
    }
  `);
  const onSubmit = useCallback(async (values: any) => {
    if (!values.bio) {
      setErrorMessage('Please tell us a bit about yourself.');
      return;
    }
    try {
      await extractSkillsForUser({
        variables: {
          input: {
            ...values
          }
        }
      });
      if (props.onComplete) {
        props.onComplete();
      }
      onClose();
    } catch (err) {
      alert(`Error: ${err.toString()}`);
    }
  }, [extractSkillsForUser, setErrorMessage]);
  useEffect(() => {
    if (bio) {
      setErrorMessage('');
    }
  }, [bio]);
  return <>
      <Button variant={'cta'} onClick={onOpen}>
        <LuWandSparkles />
        {props.ctaLabel || 'Detect My Skills'}
      </Button>
      <Dialog.Root open={isOpen} onOpenChange={({
      open
    }) => {
      if (open) {
        setIsOpen(true);
      } else if (!extractSkillsForUserMutation.loading) {
        setIsOpen(false);
      }
    }}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bgColor={'colorPalette.container'} maxWidth={'container.md'} padding={4}>
            <Dialog.Header>
              <Dialog.Title>{props.ctaLabel || 'Detect My Skills'}</Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              <VStack gap={8} justify={'stretch'}>
                <Field.Root invalid={!!errorMessage}>
                  <Field.Label htmlFor={'bio'}>Enter Your Bio (required)</Field.Label>
                  <AutoResizeTextarea disabled={extractSkillsForUserMutation.loading} id={'bio'} maxHeight={'50vh'} placeholder={'Add your bio to extract skills...'} value={bio || ''} onChange={e => setBio(e.target.value)} />
                  <Field.ErrorText>{errorMessage}</Field.ErrorText>
                </Field.Root>
                <Field.Root>
                  <Field.Label htmlFor={'bio'}>LinkedIn Profile URL</Field.Label>
                  <Input disabled={extractSkillsForUserMutation.loading} id={'linkedinUrl'} name={'linkedinUrl'} placeholder={'https://www.linkedin.com/in/you'} type={'url'} value={linkedinUrl || ''} onChange={e_0 => setLinkedinUrl(e_0.target.value)} />
                  <Text color={'colorPalette.fg/60'} fontSize={'12px'} marginTop={'0.25em'}>
                    We can attempt to load your LinkedIn profile, however access may be restricted based on
                    your privacy settings.
                  </Text>
                </Field.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Box width={'full'}>
                <Button loading={extractSkillsForUserMutation.loading} size={'lg'} variant={'cta'} width={'full'} onClick={() => onSubmit({
                bio,
                linkedinUrl
              })}>
                  <LuWandSparkles />
                  {props.ctaLabel || 'Detect My Skills'}
                </Button>
                {extractSkillsForUserMutation.loading && <Text paddingY={'1em'} textAlign={'center'}>
                    Note: this may take a few seconds to process...
                  </Text>}
              </Box>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </>;
};
function _temp(prev) {
  return [...prev, {}];
}
function _temp2() {
  return [];
}
function _temp3(userSkill, idx) {
  return <UserSkillEditorItem key={idx} index={idx} userSkill={userSkill} onChange={_temp2} />;
}